import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { adminStatsService } from '../../../services/supabaseService';

const MetricsOverview = ({ timeRange }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        const data = await adminStatsService?.getDashboardStats();
        setStats(data);
      } catch (err) {
        console.error('Failed to load metrics:', err);
        setError(err?.message);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, [timeRange]);

  const metrics = [
    {
      id: 'active_users',
      title: 'Total Customers',
      value: stats?.totalCustomers?.toLocaleString() || '—',
      change: '+8.2%',
      changeType: 'positive',
      icon: 'Users',
      color: 'primary',
      description: 'Registered customers'
    },
    {
      id: 'completed_orders',
      title: 'Total Orders',
      value: stats?.totalOrders?.toLocaleString() || '—',
      change: '+12.5%',
      changeType: 'positive',
      icon: 'CheckCircle',
      color: 'success',
      description: 'All time orders'
    },
    {
      id: 'revenue',
      title: 'Revenue (KES)',
      value: stats?.totalRevenue ? `KES ${stats?.totalRevenue?.toLocaleString()}` : '—',
      change: '+15.3%',
      changeType: 'positive',
      icon: 'DollarSign',
      color: 'accent',
      description: 'Total revenue'
    },
    {
      id: 'system_health',
      title: 'System Health',
      value: '99.8%',
      change: '-0.1%',
      changeType: 'neutral',
      icon: 'Activity',
      color: 'success',
      description: 'System uptime'
    },
    {
      id: 'active_drivers',
      title: 'Online Drivers',
      value: stats?.onlineDrivers?.toLocaleString() || '—',
      change: '+5.7%',
      changeType: 'positive',
      icon: 'Car',
      color: 'secondary',
      description: 'Drivers currently online'
    },
    {
      id: 'pending_requests',
      title: 'Active Orders',
      value: stats?.activeOrders?.toLocaleString() || '—',
      change: '-23.4%',
      changeType: 'positive',
      icon: 'Clock',
      color: 'warning',
      description: 'Orders in progress'
    }
  ];

  const formatValue = (value, id) => {
    if (id === 'revenue') {
      return `KES ${parseInt(value)?.toLocaleString()}`;
    }
    if (id === 'system_health') {
      return value;
    }
    return parseInt(value)?.toLocaleString();
  };

  const getChangeColor = (changeType) => {
    switch (changeType) {
      case 'positive': return 'text-success';
      case 'negative': return 'text-error';
      default: return 'text-text-muted';
    }
  };

  const getChangeIcon = (changeType) => {
    switch (changeType) {
      case 'positive': return 'TrendingUp';
      case 'negative': return 'TrendingDown';
      default: return 'Minus';
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-text-primary">Key Performance Indicators</h2>
        <div className="flex items-center space-x-2 text-sm text-text-muted">
          <Icon name="Clock" size={14} />
          <span>{loading ? 'Loading...' : error ? 'Error loading data' : 'Live data'}</span>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        {metrics?.map((metric) => (
          <div key={metric?.id} className="card hover:shadow-lg transition-shadow duration-200">
            <div className="flex items-start justify-between mb-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                metric?.color === 'primary' ? 'bg-primary-100' :
                metric?.color === 'success' ? 'bg-success-100' :
                metric?.color === 'accent' ? 'bg-accent-100' :
                metric?.color === 'secondary' ? 'bg-secondary-100' :
                metric?.color === 'warning'? 'bg-warning-100' : 'bg-primary-100'
              }`}>
                <Icon 
                  name={metric?.icon} 
                  size={20} 
                  color={`var(--color-${metric?.color})`}
                />
              </div>
              <div className={`flex items-center space-x-1 text-xs font-medium ${getChangeColor(metric?.changeType)}`}>
                <Icon name={getChangeIcon(metric?.changeType)} size={12} />
                <span>{metric?.change}</span>
              </div>
            </div>
            <div className="space-y-1">
              <h3 className="text-sm font-medium text-text-secondary">{metric?.title}</h3>
              <p className="text-2xl font-bold text-text-primary font-mono">
                {loading ? <span className="animate-pulse text-text-muted text-lg">...</span> : metric?.value}
              </p>
              <p className="text-xs text-text-muted">{metric?.description}</p>
            </div>
          </div>
        ))}
      </div>
      <div className="bg-surface border border-border rounded-lg p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-center">
          <div className="space-y-1">
            <p className="text-sm text-text-muted">Peak Hour</p>
            <p className="text-lg font-semibold text-text-primary">2:00 PM - 4:00 PM</p>
          </div>
          <div className="space-y-1">
            <p className="text-sm text-text-muted">Avg Response Time</p>
            <p className="text-lg font-semibold text-text-primary">2.3 minutes</p>
          </div>
          <div className="space-y-1">
            <p className="text-sm text-text-muted">Customer Satisfaction</p>
            <p className="text-lg font-semibold text-text-primary">4.8/5.0</p>
          </div>
          <div className="space-y-1">
            <p className="text-sm text-text-muted">Service Coverage</p>
            <p className="text-lg font-semibold text-text-primary">95% Nairobi</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MetricsOverview;